<template>
  <div class="flex flex-col flex-1 min-h-0 overflow-hidden bg-gray-100" @keydown.esc="openId = ''">
    <template v-if="openFile">
      <div class="flex flex-row items-center gap-2 px-3 py-2 border-b border-gray-200 bg-white flex-shrink-0">
        <button ref="backButton" type="button" class="!m-0" @click="openId = ''">
          <font-awesome-icon icon="arrow-left" class="mr-2" />
          Files
        </button>
        <p class="flex-1 min-w-0 text-sm font-medium truncate" :title="openFile.name">{{ openFile.name }}</p>
        <HelpFileChip label="Download .md" :path="openFile.path" status="ready" />
        <button type="button" class="!m-0" :disabled="pdfBusy === openFile.id" @click="downloadPdfFor(openFile)">
          {{ pdfBusy === openFile.id ? 'Preparing PDF…' : 'Download PDF' }}
        </button>
      </div>
      <div class="flex-1 min-h-0 overflow-y-auto p-4" role="region" :aria-label="openFile.name">
        <p v-if="pdfError && pdfFailed === openFile.id" class="text-sm text-red-600 mb-2">{{ pdfError }}</p>
        <p v-if="loading" class="text-sm text-gray-500">Opening file…</p>
        <p v-else-if="loadError" class="text-sm text-red-600">{{ loadError }}</p>
        <p v-else-if="!source.trim()" class="text-sm text-gray-400 italic">This file is empty.</p>
        <article v-else class="bg-white border border-gray-100 rounded p-4 text-sm text-gray-800">
          <template v-for="(piece, index) in pieces" :key="index">
            <div v-if="piece.html !== undefined" class="help-markdown" v-html="piece.html" />
            <div v-else-if="visualParts[piece.slot]" class="my-3">
              <VisualisationPart :part="visualParts[piece.slot]" @ready="(capture) => snapshotReady(piece.slot, capture)" />
            </div>
          </template>
        </article>
      </div>
    </template>
    <div v-else class="flex-1 min-h-0 overflow-y-auto p-4">
      <p class="text-sm text-gray-500 mb-3">Reports the assistant has created. Downloads use the stored copy.</p>
      <p v-if="!rows.length" class="text-sm text-gray-400 italic">No reports yet.</p>
      <div v-for="file in rows" :key="file.id" class="bg-white border border-gray-100 rounded p-3 mb-2">
        <div class="flex flex-row flex-wrap items-start gap-2">
          <div class="flex-1 min-w-0">
            <p class="text-sm font-medium break-words">{{ file.name }}</p>
            <p class="text-xs text-gray-500 mt-1">
              {{ statusLabel(file.status) }}
              <span v-if="file.createdAt"> · {{ file.createdAt }}</span>
            </p>
            <button
              v-if="file.conversationExists"
              type="button"
              class="!m-0 !px-0 !py-0 mt-1 text-xs text-primary underline"
              :aria-label="`Open conversation ${file.conversationTitle}`"
              @click="emit('open-conversation', file.conversationId)"
            >
              {{ file.conversationTitle }}
            </button>
            <p v-if="file.error" class="text-xs text-red-600 mt-1">{{ file.error }}</p>
            <p v-if="pdfError && pdfFailed === file.id" class="text-xs text-red-600 mt-1">{{ pdfError }}</p>
          </div>
          <button
            v-if="file.markdown && file.status === 'ready'"
            type="button"
            class="primary !m-0"
            @click="openId = file.id"
          >
            View
          </button>
          <HelpFileChip
            v-if="file.status === 'ready'"
            :label="file.markdown ? 'Download .md' : 'Download'"
            :path="file.path"
            status="ready"
          />
          <button
            v-if="file.markdown && file.status === 'ready'"
            type="button"
            class="!m-0"
            :disabled="pdfBusy === file.id"
            @click="downloadPdfFor(file)"
          >
            {{ pdfBusy === file.id ? 'Preparing PDF…' : 'PDF' }}
          </button>
          <button
            type="button"
            class="cancel !m-0"
            :disabled="removing === file.id"
            @click="remove(file)"
          >
            Remove
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, defineAsyncComponent, nextTick, ref, watch } from 'vue';
import { useStore } from 'vuex';
import DOMPurify from 'dompurify';
import { marked } from 'marked';
import { appConfig, callable, db } from '../../../db.js';
import { markdownPdfDefinition, pdfName } from '../../../help/markdownPdf.mjs';
import { extractVisualisation, markSlots, splitSlots, stripMermaid, visualParts as partsOf } from '../../../help/visualisations/schema.mjs';
import { createSnapshotQueue } from '../../../help/visualisations/snapshots.mjs';
import HelpFileChip from './HelpFileChip.vue';
import { generatedLibrary, statusLabel } from './helpChatFiles.mjs';
import { renderMarkdown } from './helpMessage.mjs';

const VisualisationPart = defineAsyncComponent(() => import('../../../help/visualisations/VisualisationPart.vue'));

const emit = defineEmits(['open-conversation']);

const store = useStore();
const removing = ref('');
const openId = ref('');
const backButton = ref(null);
const source = ref('');
const loading = ref(false);
const loadError = ref('');
const pdfBusy = ref('');
const pdfFailed = ref('');
const pdfError = ref('');
let request = 0;
let snapshots = null;

const rows = computed(() => generatedLibrary(store.state.helpChatFiles, store.state.helpChatList));

const openFile = computed(() => rows.value.find((file) => file.id === openId.value) || null);

const extracted = computed(() => extractVisualisation(source.value || ''));
const visualParts = computed(() => (extracted.value.data ? partsOf(extracted.value.data) : []));

function renderHtml(markdown) {
  const clean = renderMarkdown(markdown, { marked, DOMPurify });
  const body = new DOMParser().parseFromString(clean, 'text/html').body;
  for (const anchor of body.querySelectorAll('a[href]')) {
    anchor.setAttribute('target', '_blank');
    anchor.setAttribute('rel', 'noopener noreferrer');
  }
  return body.innerHTML;
}

const pieces = computed(() => {
  if (!extracted.value.data) return [{ html: renderHtml(source.value || '') }];
  return splitSlots(renderHtml(markSlots(stripMermaid(extracted.value.markdown))));
});

async function readSource(file) {
  if (typeof file.text === 'string') return file.text;
  if (!file.path) throw new Error('This file has no stored copy to open.');
  const url = await db.download(file.path);
  const response = await fetch(url);
  if (!response.ok) throw new Error(String(response.status));
  return response.text();
}

async function loadOpen() {
  const token = ++request;
  const file = openFile.value;
  source.value = '';
  loadError.value = '';
  loading.value = false;
  snapshots = null;
  if (!file) return;
  if (!file.path && typeof file.text !== 'string') {
    loadError.value = 'This file has no stored copy to open.';
    return;
  }
  loading.value = true;
  try {
    const text = await readSource(file);
    if (token !== request) return;
    source.value = text;
  } catch (error) {
    if (token !== request) return;
    console.error('[HelpChat] Could not open markdown', error);
    loadError.value = 'The file could not be opened.';
  } finally {
    if (token === request) loading.value = false;
  }
}

function snapshotReady(position, capture) {
  const file = openFile.value;
  if (!file?.visualisation || !file.conversationExists) return;
  if (!snapshots) {
    snapshots = createSnapshotQueue({
      fileId: file.id,
      conversationId: file.conversationId,
      existing: file.snapshots,
      upload: async ({ slot, data }) => {
        const { uploadSnapshot } = await import('../../../help/visualisations/capture.mjs');
        return uploadSnapshot({ conversationId: file.conversationId, fileId: file.id, slot, data });
      },
    });
  }
  snapshots.ready(position, capture);
}

async function imageData(path) {
  const response = await fetch(await db.download(path));
  if (!response.ok) throw new Error(String(response.status));
  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

async function downloadPdfFor(file) {
  if (pdfBusy.value) return;
  pdfBusy.value = file.id;
  pdfFailed.value = '';
  pdfError.value = '';
  try {
    const markdown = file.id === openId.value && source.value ? source.value : await readSource(file);
    const [{ downloadPdf }, { default: htmlToPdfmake }, { drawPart }] = await Promise.all([
      import('../../../pdfDownloader.js'),
      import('html-to-pdfmake'),
      import('../../../help/visualisations/capture.mjs'),
    ]);
    const folder = file.path.slice(0, file.path.lastIndexOf('/') + 1);
    const docDef = await markdownPdfDefinition({
      markdown,
      title: file.name,
      resolveImage: (name) => imageData(`${folder}${name}`),
      drawPart,
      marked,
      DOMPurify,
      htmlToPdfmake,
      window,
    });
    downloadPdf(docDef, pdfName(file.name), { fontSize: 10 });
  } catch (error) {
    console.error('[HelpChat] Could not create the PDF', error);
    pdfFailed.value = file.id;
    pdfError.value = 'The PDF could not be created.';
  } finally {
    pdfBusy.value = '';
  }
}

watch(openId, async (id) => {
  await loadOpen();
  if (!id) return;
  await nextTick();
  backButton.value?.focus();
});

watch(rows, () => {
  if (openId.value && !rows.value.some((file) => file.id === openId.value)) openId.value = '';
});

async function remove(file) {
  const databaseURL = appConfig?.firebaseConfig?.databaseURL;
  if (!databaseURL || !file.conversationId || removing.value) return;
  removing.value = file.id;
  try {
    await callable('helpChatFiles')({
      action: 'remove',
      conversationId: file.conversationId,
      databaseURL,
      fileId: file.id,
    });
  } catch (error) {
    console.error('[HelpChat] Could not remove file', error);
  } finally {
    removing.value = '';
  }
}
</script>

<style scoped>
.help-markdown {
  overflow-wrap: anywhere;
  line-height: 1.55;
}

.help-markdown > :deep(:first-child) {
  margin-top: 0;
}

.help-markdown > :deep(:last-child) {
  margin-bottom: 0;
}

.help-markdown :deep(p),
.help-markdown :deep(ul),
.help-markdown :deep(ol),
.help-markdown :deep(pre),
.help-markdown :deep(blockquote),
.help-markdown :deep(table) {
  margin: 0.5rem 0;
}

.help-markdown :deep(h1),
.help-markdown :deep(h2),
.help-markdown :deep(h3),
.help-markdown :deep(h4) {
  margin: 0.75rem 0 0.35rem;
  font-weight: 600;
  line-height: 1.3;
}

.help-markdown :deep(h1) { font-size: 1.15rem; }
.help-markdown :deep(h2) { font-size: 1.05rem; }
.help-markdown :deep(h3),
.help-markdown :deep(h4) { font-size: 0.95rem; }

.help-markdown :deep(ul) {
  list-style: disc;
  padding-left: 1.25rem;
}

.help-markdown :deep(ol) {
  list-style: decimal;
  padding-left: 1.25rem;
}

.help-markdown :deep(li + li) {
  margin-top: 0.15rem;
}

.help-markdown :deep(strong) {
  font-weight: 600;
}

.help-markdown :deep(a) {
  text-decoration: underline;
  text-underline-offset: 2px;
}

.help-markdown :deep(code) {
  padding: 0.05rem 0.3rem;
  border-radius: 0.25rem;
  background: rgb(0 0 0 / 6%);
  font-size: 0.8em;
}

.help-markdown :deep(pre) {
  padding: 0.6rem 0.75rem;
  overflow-x: auto;
  border-radius: 0.375rem;
  background: #111827;
  color: #f9fafb;
}

.help-markdown :deep(pre code) {
  padding: 0;
  background: transparent;
  color: inherit;
}

.help-markdown :deep(blockquote) {
  padding-left: 0.75rem;
  border-left: 3px solid #d1d5db;
  color: #4b5563;
}

.help-markdown :deep(table) {
  display: block;
  max-width: 100%;
  overflow-x: auto;
  border-collapse: collapse;
}

.help-markdown :deep(th),
.help-markdown :deep(td) {
  border: 1px solid #d1d5db;
  padding: 0.3rem 0.5rem;
  text-align: left;
  vertical-align: top;
}

.help-markdown :deep(th) {
  background: #f9fafb;
  font-weight: 600;
}

.help-markdown :deep(hr) {
  margin: 0.75rem 0;
  border-color: #e5e7eb;
}

.help-markdown :deep(img) {
  max-width: 100%;
  height: auto;
}
</style>
